/**
 * Stage 2: Logistics - Experience Creation Wizard
 *
 * Collects location and logistics information:
 * - Meeting point address with autocomplete
 * - Duration configuration (hours/days toggle)
 * - Group size limits (min/max participants)
 * - What's included checklist
 * - What to bring checklist
 * - Difficulty level and requirements (optional)
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Stories: SCRUM-193 to SCRUM-198
 * Location: apps/web/src/components/experiences/creation/stages/Stage2Logistics.jsx
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import FieldWrapper from '../../../common/forms/FieldWrapper';
import ArrayEditor from '../../../common/forms/ArrayEditor';
import FormActions from '../../../common/forms/FormActions';
import LocationAutocomplete from '../../../search/LocationAutocomplete';
import { KeyLocationsGuidedEditor } from '../../sections/guided-editor';
import useRoutePersist from '../../../../hooks/experiences/useRoutePersist';

/**
 * Stage2Logistics Component
 *
 * @param {Object} props
 * @param {Object} props.formData - Current wizard form data
 * @param {Function} props.updateField - Update single field
 * @param {Function} props.updateFormData - Update multiple fields
 * @param {Function} props.onNext - Navigate to next stage
 * @param {Function} props.onBack - Navigate to previous stage
 * @param {Object} props.validation - Validation errors for current stage
 * @param {boolean} props.isValid - Whether current stage is valid
 */
export default function Stage2Logistics({
  formData,
  updateField,
  updateFormData: _updateFormData,
  onNext,
  onBack,
  validation,
  isValid
}) {
  // Extract current values
  const {
    categoryL1Id = null,
    meetingPointAddress = '',
    meetingLat: _meetingLat = null,
    meetingLng: _meetingLng = null,
    meetingState: _meetingState = '',
    durationHours = 4,
    durationType = 'hours',
    durationDays = null,
    prepDays = 0,
    cleanupDays = 0,
    startDayRestrictions = {},
    minGroupSize = 1,
    maxGroupSize = 4,
    whatsIncluded = [],
    whatToBring = [],
    difficultyLevel = '',
    minimumAge = null,
    keyLocations = {}
  } = formData;

  const isMultiDay = durationType === 'fixed_multi_day';
  const anyDayEnabled = !startDayRestrictions?.allowed_days || startDayRestrictions.allowed_days.length === 0;

  // SCRUM-830: Get experienceId for route persistence
  const { experienceId } = useParams();

  // SCRUM-830: Route persistence hook (AC-01, AC-02, AC-03, AC-07)
  const { persistRoute, isPersisting: _isPersisting, persistError: _persistError } = useRoutePersist(experienceId);

  // SCRUM-830: Persist GPX route data when experienceId is available (AC-07: draft persistence)
  useEffect(() => {
    if (!experienceId || experienceId === 'new') {
      return;
    }

    const gpxRawXml = keyLocations?.gpx_raw_xml;
    const gpxFilename = keyLocations?.gpx_source?.filename;

    if (gpxRawXml) {
      persistRoute(gpxRawXml, gpxFilename);
    }
  }, [experienceId, keyLocations?.gpx_raw_xml, keyLocations?.gpx_source?.filename, persistRoute]);

  // SCRUM-830: Meeting point sync — keyLocations.meeting_point → top-level fields (AC-06)
  useEffect(() => {
    const mp = keyLocations?.meeting_point;
    if (!mp?.latitude || !mp?.longitude) {
      return;
    }

    // Only sync if keyLocations meeting point differs from current top-level values
    const needsSync = (
      mp.latitude !== _meetingLat ||
      mp.longitude !== _meetingLng
    );

    if (needsSync) {
      _updateFormData({
        meetingLat: mp.latitude,
        meetingLng: mp.longitude,
        meetingPointAddress: mp.suggested_name || meetingPointAddress
      });
    }
  }, [keyLocations?.meeting_point]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local state for duration unit toggle (only for hours mode)
  const [durationUnit, setDurationUnit] = useState(durationHours >= 24 ? 'days' : 'hours');
  const durationValue = durationUnit === 'days' ? durationHours / 24 : durationHours;

  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /**
   * Handle location selection from autocomplete
   */
  const handleLocationSelect = (location) => {
    _updateFormData({
      meetingPointAddress: location.displayName || location.name,
      meetingLat: location.lat,
      meetingLng: location.lng,
      meetingState: location.state || ''
    });
  };

  /**
   * Handle duration value change
   */
  const handleDurationChange = (value) => {
    const hours = durationUnit === 'days' ? value * 24 : value;
    updateField('durationHours', hours);
  };

  /**
   * Handle duration unit toggle
   */
  const handleDurationUnitChange = (unit) => {
    setDurationUnit(unit);
    // Convert current hours to new unit
    const _currentHours = durationHours;
    if (unit === 'days') {
      // Keep hours the same, just display differently
      setDurationUnit('days');
    } else {
      setDurationUnit('hours');
    }
  };

  /**
   * Handle group size increment/decrement
   */
  const adjustGroupSize = (field, delta) => {
    const currentValue = field === 'minGroupSize' ? minGroupSize : maxGroupSize;
    const newValue = Math.max(1, Math.min(20, currentValue + delta));
    updateField(field, newValue);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Logistics & Details</h2>

      <div className="space-y-6">
        {/* Meeting Point Address - SCRUM-193 */}
        <FieldWrapper
          label="Meeting Point"
          htmlFor="meetingPoint"
          required={true}
          error={validation?.errors?.meetingPointAddress}
          helperText="Where will guests meet you to start the experience?"
        >
          <LocationAutocomplete
            value={meetingPointAddress}
            onChange={(value) => updateField('meetingPointAddress', value)}
            onSelect={handleLocationSelect}
            placeholder="Enter a city, address, or landmark"
            country="US"
            className="w-full"
          />
        </FieldWrapper>

        {/* Duration Type Selector - SCRUM-405 (MVP 1.4.2) */}
        <FieldWrapper
          label="Duration Type"
          htmlFor="durationType"
          required={true}
          helperText="Is this a single-day or multi-day experience?"
        >
          <div className="flex border border-gray-300 rounded-lg overflow-hidden w-fit">
            <button
              type="button"
              onClick={() => {
                updateField('durationType', 'hours');
                updateField('durationDays', null);
                updateField('prepDays', 0);
                updateField('cleanupDays', 0);
                updateField('startDayRestrictions', {});
              }}
              className={`px-4 py-2 font-medium transition-colors ${
                !isMultiDay
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Hours (single day)
            </button>
            <button
              type="button"
              onClick={() => updateField('durationType', 'fixed_multi_day')}
              className={`px-4 py-2 font-medium transition-colors border-l border-gray-300 ${
                isMultiDay
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Fixed multi-day trip
            </button>
          </div>
        </FieldWrapper>

        {/* Duration Configuration - SCRUM-194 (hours mode) */}
        {!isMultiDay && (
          <div>
            <FieldWrapper
              label="Duration"
              htmlFor="duration"
              required={true}
              error={validation?.errors?.durationHours}
              helperText="How long does this experience last?"
            >
              <div className="flex gap-3">
                <input
                  id="duration"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={durationValue}
                  onChange={(e) => handleDurationChange(parseFloat(e.target.value) || 0)}
                  className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleDurationUnitChange('hours')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      durationUnit === 'hours'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Hours
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDurationUnitChange('days')}
                    className={`px-4 py-2 font-medium transition-colors border-l border-gray-300 ${
                      durationUnit === 'days'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Days
                  </button>
                </div>
              </div>
            </FieldWrapper>
          </div>
        )}

        {/* Multi-Day Duration Configuration - SCRUM-405 */}
        {isMultiDay && (
          <div className="space-y-4">
            <FieldWrapper
              label="Trip Duration (Days)"
              htmlFor="durationDays"
              required={true}
              error={validation?.errors?.durationDays}
              helperText="How many days is this trip? (2-14 days)"
            >
              <input
                id="durationDays"
                type="number"
                min="2"
                max="14"
                step="1"
                value={durationDays || ''}
                onChange={(e) => updateField('durationDays', e.target.value ? parseInt(e.target.value) : null)}
                className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g., 5"
              />
            </FieldWrapper>

            {/* Prep/Cleanup Bookend Days - SCRUM-406 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldWrapper
                label="Prep Days (before trip)"
                htmlFor="prepDays"
                helperText="Days blocked on your calendar before the trip starts (0-3)"
              >
                <select
                  id="prepDays"
                  value={prepDays}
                  onChange={(e) => updateField('prepDays', parseInt(e.target.value))}
                  className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="0">0 days</option>
                  <option value="1">1 day</option>
                  <option value="2">2 days</option>
                  <option value="3">3 days</option>
                </select>
              </FieldWrapper>

              <FieldWrapper
                label="Cleanup Days (after trip)"
                htmlFor="cleanupDays"
                helperText="Days blocked on your calendar after the trip ends (0-3)"
              >
                <select
                  id="cleanupDays"
                  value={cleanupDays}
                  onChange={(e) => updateField('cleanupDays', parseInt(e.target.value))}
                  className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="0">0 days</option>
                  <option value="1">1 day</option>
                  <option value="2">2 days</option>
                  <option value="3">3 days</option>
                </select>
              </FieldWrapper>
            </div>

            {/* Total block calculation */}
            {durationDays && durationDays >= 2 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  {prepDays > 0 ? `${prepDays} prep + ` : ''}
                  {durationDays} trip{cleanupDays > 0 ? ` + ${cleanupDays} cleanup` : ''} = <strong>{(prepDays || 0) + durationDays + (cleanupDays || 0)} days blocked on your calendar</strong>
                </p>
              </div>
            )}

            {/* Start Day Restrictions - SCRUM-407 */}
            <FieldWrapper
              label="Start Day Restrictions"
              htmlFor="startDayRestrictions"
              helperText="Limit which days of the week this trip can start"
            >
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={anyDayEnabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateField('startDayRestrictions', {});
                      } else {
                        updateField('startDayRestrictions', { allowed_days: [] });
                      }
                    }}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Any day (no restrictions)</span>
                </label>

                {!anyDayEnabled && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {DAY_NAMES.map((name, index) => {
                      const isSelected = (startDayRestrictions?.allowed_days || []).includes(index);
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            const current = startDayRestrictions?.allowed_days || [];
                            const updated = isSelected
                              ? current.filter(d => d !== index)
                              : [...current, index].sort((a, b) => a - b);
                            updateField('startDayRestrictions', { allowed_days: updated });
                          }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                            isSelected
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </FieldWrapper>
          </div>
        )}

        {/* Group Size Limits - SCRUM-195 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Min Group Size */}
          <FieldWrapper
            label="Minimum Group Size"
            htmlFor="minGroupSize"
            required={true}
            helperText="Smallest number of guests"
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => adjustGroupSize('minGroupSize', -1)}
                disabled={minGroupSize <= 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                −
              </button>
              <input
                id="minGroupSize"
                type="number"
                min="1"
                max="20"
                value={minGroupSize}
                onChange={(e) => updateField('minGroupSize', parseInt(e.target.value) || 1)}
                className="w-20 px-4 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => adjustGroupSize('minGroupSize', 1)}
                disabled={minGroupSize >= 20}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                +
              </button>
            </div>
          </FieldWrapper>

          {/* Max Group Size */}
          <FieldWrapper
            label="Maximum Group Size"
            htmlFor="maxGroupSize"
            required={true}
            error={validation?.errors?.maxGroupSize}
            helperText="Largest number of guests"
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => adjustGroupSize('maxGroupSize', -1)}
                disabled={maxGroupSize <= 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                −
              </button>
              <input
                id="maxGroupSize"
                type="number"
                min="1"
                max="20"
                value={maxGroupSize}
                onChange={(e) => updateField('maxGroupSize', parseInt(e.target.value) || 1)}
                className="w-20 px-4 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => adjustGroupSize('maxGroupSize', 1)}
                disabled={maxGroupSize >= 20}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                +
              </button>
            </div>
          </FieldWrapper>
        </div>

        {/* What's Included - SCRUM-196 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What&apos;s Included
            <span className="text-gray-500 font-normal ml-2">(Optional, 3+ recommended)</span>
          </label>
          <ArrayEditor
            items={whatsIncluded}
            onChange={(updated) => updateField('whatsIncluded', updated)}
            renderItem={(item) => (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            )}
            maxItems={20}
            minItems={0}
            allowReorder={false}
            showCount={true}
            addPlaceholder="e.g., All necessary equipment, Snacks and drinks, Professional guide"
            emptyMessage="Add items that are included with this experience."
          />
        </div>

        {/* What to Bring - SCRUM-197 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What to Bring
            <span className="text-gray-500 font-normal ml-2">(Optional, 3+ recommended)</span>
          </label>
          <ArrayEditor
            items={whatToBring}
            onChange={(updated) => updateField('whatToBring', updated)}
            renderItem={(item) => (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            )}
            maxItems={20}
            minItems={0}
            allowReorder={false}
            showCount={true}
            addPlaceholder="e.g., Water bottle, Sunscreen, Comfortable hiking shoes"
            emptyMessage="Add items guests should bring with them."
          />
        </div>

        {/* Key Locations Guided Editor - MVP 1.20.6-S */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Locations</h3>
          <p className="text-sm text-gray-600 mb-4">
            Set meeting point and optionally import GPX route with waypoints.
          </p>
          <KeyLocationsGuidedEditor
            experienceId="new"
            categoryL1Id={categoryL1Id}
            initialData={keyLocations}
            context="wizard"
            onSave={(data) => updateField('keyLocations', data)}
            onCancel={() => {}}
            wizardUpdateField={updateField}
          />
        </div>

        {/* Difficulty & Requirements - SCRUM-198 */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Requirements (Optional)</h3>

          <div className="space-y-4">
            {/* Difficulty Level */}
            <FieldWrapper
              label="Difficulty Level"
              htmlFor="difficultyLevel"
              helperText="Help guests understand the physical demands"
            >
              <select
                id="difficultyLevel"
                value={difficultyLevel}
                onChange={(e) => updateField('difficultyLevel', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Select difficulty (optional)...</option>
                <option value="easy">Easy - Suitable for all fitness levels</option>
                <option value="moderate">Moderate - Some physical activity required</option>
                <option value="challenging">Challenging - Good fitness level needed</option>
                <option value="strenuous">Strenuous - High fitness level required</option>
              </select>
            </FieldWrapper>

            {/* Minimum Age */}
            <FieldWrapper
              label="Minimum Age"
              htmlFor="minimumAge"
              helperText="Age restriction for this experience (leave blank if none)"
            >
              <input
                id="minimumAge"
                type="number"
                min="0"
                max="100"
                value={minimumAge || ''}
                onChange={(e) => updateField('minimumAge', e.target.value ? parseInt(e.target.value) : null)}
                className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g., 18"
              />
            </FieldWrapper>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <FormActions
        onCancel={onBack}
        onSubmit={onNext}
        hasUnsavedChanges={true}
        isSaving={false}
        cancelLabel="Back"
        submitLabel="Continue to Pricing"
        showSaveDraft={false}
        disableSubmit={!isValid}
        submitVariant="success"
        layout="split"
      />
    </div>
  );
}
